import React from 'react';
import { Card, LoadingSpinner } from 'components/common';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtDate = (iso) => {
  if (!iso) return '—';
  const [y, m, d] = String(iso).split('-');
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d}-${MONTHS[parseInt(m, 10) - 1]}-${y}`;
};

const BUCKET_COLORS = { '1-7': '#3B82F6', '8-30': '#F59E0B', '31-90': '#F97316', '91+': '#EF4444' };

const delayBadge = (days) => {
  if (days <= 7)  return 'tw-bg-blue-100 tw-text-blue-700';
  if (days <= 30) return 'tw-bg-amber-100 tw-text-amber-700';
  if (days <= 90) return 'tw-bg-orange-100 tw-text-orange-700';
  return 'tw-bg-red-100 tw-text-red-700';
};

// Days-overdue window per aging bucket → commitment date range (today - days)
const BUCKET_WINDOWS = {
  '1-7':   { from: -7,  to: -1 },
  '8-30':  { from: -30, to: -8 },
  '31-90': { from: -90, to: -31 },
  '91+':   { from: null, to: -91 },
};

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

// Overdue waivers: aging distribution + worst records.
// Clicking an aging bucket drills into that bucket's records.
const WaiverOverdueCard = ({ data, loading, error, onDrillDown }) => {
  const drillBucket = (b) => {
    if (!onDrillDown || b.count === 0) return;
    const win   = BUCKET_WINDOWS[b.bucket] || {};
    const extra = { overdue: 'true' };
    if (win.from != null) extra.commitment_from = isoDaysAgo(win.from);
    if (win.to   != null) extra.commitment_to   = isoDaysAgo(win.to);
    onDrillDown(`Overdue ${b.label}`, extra);
  };
  const aging   = data?.aging || [];
  const records = data?.records || [];
  const total   = data?.count || 0;
  const maxBucket = Math.max(...aging.map(b => b.count), 1);

  return (
    <Card padding="none" className="tw-border tw-border-gray-200 tw-shadow-sm">
      <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-100 tw-flex tw-items-center tw-gap-2">
        <FiAlertTriangle size={15} className="tw-text-orange-500" />
        <h3 className="tw-font-bold tw-text-gray-900 tw-text-sm tw-mb-0">Overdue Waivers</h3>
        {total > 0 && (
          <span className="tw-text-[10px] tw-font-bold tw-px-2 tw-py-0.5 tw-rounded-xl tw-bg-orange-100 tw-text-orange-600">{fmt(total)}</span>
        )}
        <span className="tw-text-[10px] tw-text-gray-400 tw-ml-auto">approved · past commitment date · not closed</span>
      </div>

      {loading ? (
        <div className="tw-flex tw-justify-center tw-py-10"><LoadingSpinner size="md" /></div>
      ) : error ? (
        <p className="tw-text-xs tw-text-gray-400 tw-text-center tw-py-10">Failed to load overdue data</p>
      ) : total === 0 ? (
        <div className="tw-flex tw-items-center tw-gap-2 tw-justify-center tw-py-8 tw-text-emerald-600">
          <FiCheckCircle size={16} />
          <p className="tw-text-sm tw-font-semibold tw-mb-0">No overdue waivers</p>
        </div>
      ) : (
        <div className="tw-p-5 tw-space-y-4">
          {/* Aging distribution */}
          <div className="tw-space-y-2">
            <p className="tw-text-xs tw-font-bold tw-text-gray-500 tw-uppercase tw-tracking-widest tw-mb-1">Aging Distribution</p>
            {aging.map(b => (
              <div key={b.bucket}
                onClick={() => drillBucket(b)}
                className={`tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-px-1 -tw-mx-1 ${onDrillDown && b.count > 0 ? 'tw-cursor-pointer hover:tw-bg-slate-50' : ''}`}>
                <span className="tw-text-xs tw-text-gray-500 tw-w-16 tw-shrink-0">{b.label}</span>
                <div className="tw-flex-1 tw-bg-gray-100 tw-rounded-xl tw-h-3.5 tw-overflow-hidden">
                  <div className="tw-h-full tw-rounded-xl tw-transition-all"
                    style={{ width: `${Math.max((b.count / maxBucket) * 100, b.count > 0 ? 2 : 0)}%`, background: BUCKET_COLORS[b.bucket] }} />
                </div>
                <span className="tw-text-xs tw-font-bold tw-w-8 tw-text-right tw-tabular-nums" style={{ color: BUCKET_COLORS[b.bucket] }}>{b.count}</span>
              </div>
            ))}
          </div>

          {/* Worst records — fixed height with internal scroll */}
          <div className="tw-overflow-auto tw-max-h-[320px] tw-border tw-border-gray-100 tw-rounded">
            <table className="tw-w-full tw-text-xs tw-border-collapse">
              <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="tw-bg-gray-50">
                  <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Waiver</th>
                  <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100 tw-hidden md:tw-table-cell">Project</th>
                  <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">Commitment</th>
                  <th className="tw-text-center tw-px-3 tw-py-2 tw-font-semibold tw-text-orange-600 tw-border-b tw-border-gray-100">Overdue</th>
                  <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100">CSE Incharge</th>
                  <th className="tw-text-left tw-px-3 tw-py-2 tw-font-semibold tw-text-gray-500 tw-border-b tw-border-gray-100 tw-hidden md:tw-table-cell">Unit</th>
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id} className="hover:tw-bg-orange-50/40 tw-transition-colors">
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50">
                      {r.deal_id ? (
                        <Link to={`/crm/projects/${r.deal_id}`} target="_blank" rel="noreferrer"
                          className="tw-font-mono tw-font-bold tw-text-blue-700 hover:tw-underline">
                          {r.waiver_number}
                        </Link>
                      ) : <span className="tw-font-mono tw-font-bold tw-text-gray-700">{r.waiver_number}</span>}
                      {r.j_number && <span className="tw-text-gray-400 tw-ml-1.5">{r.j_number}</span>}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-700 tw-max-w-[200px] tw-truncate tw-hidden md:tw-table-cell" title={r.project_name}>
                      {r.project_name || '—'}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center tw-text-gray-600">{fmtDate(r.commitment_date)}</td>
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-center">
                      <span className={`tw-px-2 tw-py-0.5 tw-rounded-xl tw-text-xs tw-font-semibold ${delayBadge(r.days_overdue)}`}>
                        {r.days_overdue}d
                      </span>
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-700">
                      {(r.cse_incharge || []).join(', ') || '—'}
                    </td>
                    <td className="tw-px-3 tw-py-2 tw-border-b tw-border-gray-50 tw-text-gray-500 tw-hidden md:tw-table-cell">{r.unit_name || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
};

WaiverOverdueCard.propTypes = {
  data: PropTypes.object, loading: PropTypes.bool, error: PropTypes.any,
  onDrillDown: PropTypes.func,
};
WaiverOverdueCard.defaultProps = { data: null, loading: false, error: null, onDrillDown: null };

export default WaiverOverdueCard;
